
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Product, BillingPeriod } from '@/types/product';
import { createProduct } from '@/services/productMutations';
import { isValidRecurringBillingPeriod } from '@/utils/billingPeriodUtils';
import { isSubscription } from '@/lib/utils';
import { useGlobalEvents } from './useGlobalEvents.ts';
import { useProductAlert } from '@/contexts/ProductAlertContext';
import { supabase } from '@/integrations/supabase/client';

export interface ProductFormState {
  title: string;
  description: string;
  amount: string;
  billing_period: BillingPeriod;
}

export const useUnifiedProductForm = (onClose?: () => void) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { dispatchProductCreated } = useGlobalEvents();
  const { openProductAlert } = useProductAlert();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [billing_period, setBillingPeriod] = useState<BillingPeriod>('one_time');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatingStripeProduct, setCreatingStripeProduct] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [createdProductId, setCreatedProductId] = useState<string | null>(null);

  const validateForm = () => {
    if (!title) {
      toast({
        title: "Missing information",
        description: "Please enter a product title",
        variant: "destructive"
      });
      return false;
    }
    
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount greater than 0",
        variant: "destructive"
      });
      return false;
    }
    
    // Enhanced validation: Check if this is a recurring product (not one_time)
    // and ensure it has a valid recurring billing period
    const isSubscriptionProduct = billing_period !== 'one_time';
    const hasValidRecurringPeriod = isValidRecurringBillingPeriod(billing_period);
    
    if (isSubscriptionProduct && !hasValidRecurringPeriod) {
      setValidationError("Subscription products require a recurring billing period (monthly, yearly, etc.)");
      toast({
        title: "Invalid billing period",
        description: "Subscription products require a recurring billing period (monthly, yearly, etc.)",
        variant: "destructive"
      });
      return false;
    }
    
    setValidationError(null);
    return true;
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setAmount('');
    setBillingPeriod('one_time');
    setValidationError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let useAnchorBilling = false;
      if (isSubscription(billing_period)) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('anchor_billing_enabled')
            .eq('id', user.id)
            .single();
          useAnchorBilling = Boolean(profile?.anchor_billing_enabled);
        }
      }

      const product: Omit<Product, 'id' | 'created_at' | 'status'> = {
        title,
        description: description || '',
        amount: Number(amount) || 0,
        billing_period,
        hero_image: localStorage.getItem('invoiceHeroImage') || 'business',
        is_subscription: isSubscription(billing_period),
        use_anchor_billing: useAnchorBilling,
        anchor_billing_day: useAnchorBilling ? 1 : undefined,
        user_id: '' // This will be set in the backend
      };
      
      setCreatingStripeProduct(true);
      
      const savedProduct = await createProduct(product);
      
      if (!savedProduct) {
        throw new Error("Failed to save product to database");
      }
      
      localStorage.setItem('hasCreatedFirstProduct', 'true');
      
      toast({
        title: billing_period === 'one_time' ? "One-time purchase created!" : "Subscription created!",
        description: "Your product has been created successfully",
        variant: "success",
        className: "border-none",
      });
      
      setCreatedProductId(savedProduct.id);

      openProductAlert(savedProduct.id);
      
      dispatchProductCreated(savedProduct);

      if (onClose){
        onClose();
      } else {
        navigate('/dashboard')
      }

    } catch (error) {
      console.error('Failed to create product:', error);
      toast({
        title: "Error creating product",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCreatingStripeProduct(false);
      setIsSubmitting(false);
    }
  };

  return {
    title,
    setTitle,
    description,
    setDescription,
    amount,
    setAmount,
    billing_period,
    setBillingPeriod,
    isSubmitting,
    creatingStripeProduct,
    validationError,
    handleSubmit,
    resetForm
  };
};
